import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
// # Import map_iso utilities
import {
  getCountryISO,
  getCountryNameFromISO,
  isValidISO,
  getCountryData,
  formatCountryDataForHeatMap,
  groupCountryDataByRegion,
  getISOFromAnyFormat,
  getCountryRegion,
  getCountryAlternativeNames
} from './map_iso';
import './financial_heat.css';

// # Load world geography data - use a reliable and CORS-enabled source
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// # Create mapping using official ISO_A3 codes from world-atlas
// # Note: This is kept for backward compatibility, but we'll prefer map_iso.js
const countryNameMapping = {
  // # Arab Countries
  'EGYPT': 'EGY',
  'ALGERIA': 'DZA',
  'BAHRAIN': 'BHR',
  'IRAQ': 'IRQ',
  'JORDAN': 'JOR',
  'KUWAIT': 'KWT',
  'LEBANON': 'LBN',
  'LIBYA': 'LBY',
  'MOROCCO': 'MAR',
  'OMAN': 'OMN',
  'PALESTINE': 'PSE',
  'QATAR': 'QAT',
  'SAUDI ARABIA': 'SAU',
  'SOMALIA': 'SOM',
  'SUDAN': 'SDN',
  'SYRIA': 'SYR',
  'TUNISIA': 'TUN',
  'UNITED ARAB EMIRATES': 'ARE',
  'YEMEN': 'YEM',
  
  // # Asia
  'AFGHANISTAN': 'AFG',
  'BANGLADESH': 'BGD',
  'BRUNEI': 'BRN',
  'CAMBODIA': 'KHM',
  'CHINA': 'CHN',
  'INDIA': 'IND',
  'INDONESIA': 'IDN',
  'IRAN': 'IRN',
  'JAPAN': 'JPN',
  'KAZAKHSTAN': 'KAZ',
  'SOUTH KOREA': 'KOR',
  'NORTH KOREA': 'PRK',
  'MALAYSIA': 'MYS',
  'PAKISTAN': 'PAK',
  'PHILIPPINES': 'PHL',
  'SINGAPORE': 'SGP',
  'SRI LANKA': 'LKA',
  'TAIWAN': 'TWN',
  'THAILAND': 'THA',
  'TURKEY': 'TUR',
  'VIETNAM': 'VNM',
  'KYRGYZSTAN': 'KGZ',
  'NEPAL': 'NPL',
  'TIMOR_LESTE': 'TLS',
  'TURKMENISTAN': 'TKM',
  
  // # Europe
  'UNITED KINGDOM': 'GBR',
  'GERMANY': 'DEU',
  'FRANCE': 'FRA',
  'ITALY': 'ITA',
  'SPAIN': 'ESP',
  'RUSSIA': 'RUS',
  'SLOVAKIA': 'SVK',
  'NETHERLANDS': 'NLD',
  'GREECE': 'GRC',
  
  // # Americas
  'UNITED STATES': 'USA',
  'CANADA': 'CAN',
  'MEXICO': 'MEX',
  'BRAZIL': 'BRA',
  'PERU': 'PER',
  'COSTA_RICA': 'CRI',
  
  // # Africa
  'TANZANIA': 'TZA',
  
  // # Oceania
  'AUSTRALIA': 'AUS',
  'NEW ZEALAND': 'NZL',
  'PAPUA_NEW_GUINEA': 'PNG'
};

// # Create reverse mapping for display names
const isoToCountryName = Object.entries(countryNameMapping).reduce((acc, [name, iso]) => {
  // # Use the first occurrence of each ISO code (preferring the simpler name)
  if (!acc[iso]) {
    acc[iso] = name.charAt(0) + name.slice(1).toLowerCase();
  }
  return acc;
}, {});

// # Add ISO numeric code mapping for countries
const numericIsoMapping = {
  '004': 'AFG', // Afghanistan
  '008': 'ALB', // Albania
  '012': 'DZA', // Algeria
  '031': 'AZE', // Azerbaijan
  '051': 'ARM', // Armenia
  '112': 'BLR', // Belarus
  '156': 'CHN', // China
  '196': 'CYP', // Cyprus
  '203': 'CZE', // Czech Republic
  '208': 'DNK', // Denmark
  '246': 'FIN', // Finland
  '250': 'FRA', // France
  '268': 'GEO', // Georgia
  '276': 'DEU', // Germany
  '300': 'GRC', // Greece
  '348': 'HUN', // Hungary
  '352': 'ISL', // Iceland
  '356': 'IND', // India
  '360': 'IDN', // Indonesia
  '364': 'IRN', // Iran
  '368': 'IRQ', // Iraq
  '372': 'IRL', // Ireland
  '376': 'ISR', // Israel
  '380': 'ITA', // Italy
  '392': 'JPN', // Japan
  '398': 'KAZ', // Kazakhstan
  '400': 'JOR', // Jordan
  '404': 'KEN', // Kenya
  '410': 'KOR', // South Korea
  '414': 'KWT', // Kuwait
  '417': 'KGZ', // Kyrgyzstan
  '422': 'LBN', // Lebanon
  '428': 'LVA', // Latvia
  '440': 'LTU', // Lithuania
  '458': 'MYS', // Malaysia
  '484': 'MEX', // Mexico
  '498': 'MDA', // Moldova
  '504': 'MAR', // Morocco
  '512': 'OMN', // Oman
  '528': 'NLD', // Netherlands
  '554': 'NZL', // New Zealand
  '578': 'NOR', // Norway
  '586': 'PAK', // Pakistan
  '608': 'PHL', // Philippines
  '616': 'POL', // Poland
  '620': 'PRT', // Portugal
  '634': 'QAT', // Qatar
  '642': 'ROU', // Romania
  '643': 'RUS', // Russia
  '682': 'SAU', // Saudi Arabia
  '688': 'SRB', // Serbia
  '702': 'SGP', // Singapore
  '703': 'SVK', // Slovakia
  '704': 'VNM', // Vietnam
  '710': 'ZAF', // South Africa
  '724': 'ESP', // Spain
  '752': 'SWE', // Sweden
  '756': 'CHE', // Switzerland
  '760': 'SYR', // Syria
  '764': 'THA', // Thailand
  '784': 'ARE', // United Arab Emirates
  '792': 'TUR', // Turkey
  '804': 'UKR', // Ukraine
  '818': 'EGY', // Egypt
  '826': 'GBR', // United Kingdom
  '840': 'USA', // United States
  '887': 'YEM', // Yemen
  '894': 'ZMB'  // Zambia
};

const FinancialHeatMap = () => {
  // # State for map data and UI
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [mapPosition, setMapPosition] = useState({ coordinates: [0, 20], zoom: 1 });
  const [financialData, setFinancialData] = useState({
    revenueByCountry: {},
    revenueByISO: {},
    revenueByRegion: {},
    productsByCountry: {},
    paymentCountByCountry: {},
    paymentCountByISO: {},
    paymentCountByRegion: {},
    totalRevenue: 0,
    totalPayments: 0
  });
  const [transformedData, setTransformedData] = useState({});
  const [transformedPaymentData, setTransformedPaymentData] = useState({});
  const mapContainerRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });
  const [filterText, setFilterText] = useState('');
  const [regionFilter, setRegionFilter] = useState('All');

  // # Get unique regions for filter dropdown
  const uniqueRegions = useMemo(() => {
    if (!financialData.revenueByCountry) return ['All'];
    const regions = new Set(['All']);
    Object.keys(financialData.revenueByCountry).forEach(country => {
      const region = getCountryRegion(country);
      if (region && region !== 'Unknown') regions.add(region);
    });
    return Array.from(regions).sort();
  }, [financialData.revenueByCountry]);

  // # Sort function for table data
  const sortData = useCallback((data) => {
    return [...data].sort((a, b) => {
      const [countryA, revenueA] = a;
      const [countryB, revenueB] = b;
      
      switch (sortConfig.key) {
        case 'country':
          return sortConfig.direction === 'asc' 
            ? countryA.localeCompare(countryB)
            : countryB.localeCompare(countryA);
        case 'region':
          const regionA = getCountryRegion(countryA) || 'Unknown';
          const regionB = getCountryRegion(countryB) || 'Unknown';
          return sortConfig.direction === 'asc'
            ? regionA.localeCompare(regionB)
            : regionB.localeCompare(regionA);
        case 'revenue':
          return sortConfig.direction === 'asc'
            ? revenueA - revenueB
            : revenueB - revenueA;
        case 'payments':
          const paymentsA = financialData.paymentCountByCountry?.[countryA] || 0;
          const paymentsB = financialData.paymentCountByCountry?.[countryB] || 0;
          return sortConfig.direction === 'asc'
            ? paymentsA - paymentsB
            : paymentsB - paymentsA;
        case 'percentage':
          const percentA = (revenueA / financialData.totalRevenue) * 100;
          const percentB = (revenueB / financialData.totalRevenue) * 100;
          return sortConfig.direction === 'asc'
            ? percentA - percentB
            : percentB - percentA;
        default:
          return 0;
      }
    });
  }, [sortConfig, financialData.paymentCountByCountry, financialData.totalRevenue]);

  // # Handle sort click
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // # Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!financialData.revenueByCountry) return [];
    
    let data = Object.entries(financialData.revenueByCountry);
    
    // # Apply text filter
    if (filterText) {
      const searchText = filterText.toLowerCase();
      data = data.filter(([country]) => {
        const region = getCountryRegion(country) || 'Unknown';
        return country.toLowerCase().includes(searchText) ||
               region.toLowerCase().includes(searchText);
      });
    }
    
    // # Apply region filter
    if (regionFilter !== 'All') {
      data = data.filter(([country]) => {
        const region = getCountryRegion(country);
        return region === regionFilter;
      });
    }
    
    // # Sort data
    return sortData(data);
  }, [financialData.revenueByCountry, filterText, regionFilter, sortData]);

  // # Load geography data
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    
    const loadGeoData = async () => {
      try {
        const response = await fetch(geoUrl);
        
        if (!response.ok) {
          throw new Error(`Failed to load geography data: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        setGeoData(data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading geography data:', err);
        setError(err.message || 'Failed to load map data');
        setIsLoading(false);
        
        // # Fallback to a static data source if the primary source fails
        try {
          console.log('Attempting to load fallback geography data');
          const fallbackResponse = await fetch('https://unpkg.com/world-atlas@2.0.2/countries-110m.json');
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            console.log('Fallback geography data loaded successfully');
            setGeoData(fallbackData);
            setError(null);
          }
        } catch (fallbackErr) {
          console.error('Fallback geography data also failed:', fallbackErr);
        }
      }
    };
    
    loadGeoData();
  }, []);

  // # Fetch financial data from Firestore
  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        setIsLoading(true);
        const startTime = performance.now();
        
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const querySnapshot = await getDocs(financialRef);
        
        // # Initialize data structures
        let totalRevenue = 0;
        let revenueByCountry = {};
        let productsByCountry = {};
        let paymentCountByCountry = {}; // # Add payment count tracking
        let transactionsCount = 0;
        let paymentsByDate = [];
        
        // # Process each document (year_month format)
        querySnapshot.forEach(doc => {
          const financialData = doc.data();
          
          // # Process payments in the document
          if (financialData.payments) {
            Object.entries(financialData.payments).forEach(([paymentId, payment]) => {
              // # Extract user data and payment details
              const userData = payment.user;
              const payData = payment.pay;
              
              if (userData && payData && payData.egp_total) {
                const country = userData.nationality || 'Unknown';
                const amount = Number(payData.egp_total) || 0;
                const product = payData.product || 'Unknown';
                const date = payment.metadata?.created_at || new Date().toISOString();
                
                // # Update total revenue
                totalRevenue += amount;
                
                // # Update country data
                if (!revenueByCountry[country]) {
                  revenueByCountry[country] = 0;
                  productsByCountry[country] = {};
                  paymentCountByCountry[country] = 0; // # Initialize payment count
                }
                
                revenueByCountry[country] += amount;
                paymentCountByCountry[country]++; // # Increment payment count for country
                
                // # Update product data
                if (!productsByCountry[country][product]) {
                  productsByCountry[country][product] = 0;
                }
                productsByCountry[country][product] += amount;
                
                // # Add to payments by date
                paymentsByDate.push({
                  id: paymentId,
                  country: country,
                  amount: amount,
                  date: date,
                  product: product
                });
                
                transactionsCount++;
              }
            });
          }
        });
        
        // # Format data for heat map using map_iso utilities
        const revenueByISO = formatCountryDataForHeatMap(revenueByCountry, {
          useISO: true,
          defaultValue: 0,
          includeEmptyCountries: false,
          preserveUnmappedCountries: true
        });
        
        // # Format payment count data for map
        const paymentCountByISO = formatCountryDataForHeatMap(paymentCountByCountry, {
          useISO: true,
          defaultValue: 0,
          includeEmptyCountries: false,
          preserveUnmappedCountries: true
        });
        
        // # Group data by region
        const revenueByRegion = groupCountryDataByRegion(revenueByCountry);
        const paymentCountByRegion = groupCountryDataByRegion(paymentCountByCountry);
        
        // # Update state with processed data
        setFinancialData({
          revenueByCountry,
          revenueByISO,
          revenueByRegion,
          productsByCountry,
          paymentCountByCountry,
          paymentCountByISO,
          paymentCountByRegion,
          totalRevenue,
          totalPayments: transactionsCount
        });
        
        console.log('Financial data processed:', {
          revenueByCountry,
          revenueByISO,
          revenueByRegion,
          productsByCountry,
          paymentCountByCountry,
          totalRevenue,
          totalPayments: transactionsCount
        });
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching financial data:', error);
        setError('Failed to load financial data.');
        setIsLoading(false);
      }
    };
    
    fetchFinancialData();
  }, []);

  // # Transform data to match topojson country ISO codes
  useEffect(() => {
    if (!financialData.revenueByISO) return;
    
    const newTransformedData = {};
    const newTransformedPaymentData = {}; // # Add new object for payment data
    
    // # Add direct mappings for specific countries that might be missing
    Object.entries(financialData.revenueByCountry || {}).forEach(([countryName, amount]) => {
      // # Get ISO code using the enhanced function
      const isoCode = getISOFromAnyFormat(countryName);
      
      if (isoCode !== "Unknown") {
        // # Add data under ISO code
        newTransformedData[isoCode] = amount;
        
        // # Add payment count mapping if available
        if (financialData.paymentCountByCountry && financialData.paymentCountByCountry[countryName]) {
          newTransformedPaymentData[isoCode] = financialData.paymentCountByCountry[countryName];
        }
        
        // # Also add under country name variations for matching
        const countryData = getCountryData(isoCode);
        if (countryData) {
          newTransformedData[countryData.name] = amount;
          if (countryData.alternativeNames) {
            countryData.alternativeNames.forEach(altName => {
              newTransformedData[altName] = amount;
            });
          }
        }
      }
      
      // # Keep original name mapping as fallback
      newTransformedData[countryName] = amount;
      newTransformedData[countryName.toUpperCase()] = amount;
    });
    
    // # Add any ISO-based revenue data that wasn't added from country names
    Object.entries(financialData.revenueByISO || {}).forEach(([isoCode, amount]) => {
      if (!newTransformedData[isoCode]) {
        newTransformedData[isoCode] = amount;
        
        // # Get country name for this ISO and add those mappings too
        const countryName = getCountryNameFromISO(isoCode);
        if (countryName && countryName !== 'Unknown') {
          newTransformedData[countryName] = amount;
          newTransformedData[countryName.toUpperCase()] = amount;
        }
      }
    });
    
    console.log('Transformed financial data for map:', newTransformedData);
    console.log('Transformed payment data for map:', newTransformedPaymentData);
    
    setTransformedData(newTransformedData);
    setTransformedPaymentData(newTransformedPaymentData);
  }, [financialData.revenueByISO, financialData.revenueByCountry, financialData.paymentCountByCountry]);

  // # Find max revenue for color scaling (ensure it's not negative or zero)
  const maxRevenue = Math.max(1, ...Object.values(transformedData));

  // # Create color scale with multiple steps for better visualization
  const colorScale = scaleLinear()
    .domain([0, maxRevenue * 0.2, maxRevenue * 0.4, maxRevenue * 0.6, maxRevenue * 0.8, maxRevenue])
    .range(['#f5f5f5', '#e5f5e5', '#c5e5c5', '#a5d5a5', '#85c585', '#65b565']);

  // # Handle mouse events for tooltip
  const handleMouseEnter = (geo, event) => {
    if (!mapContainerRef.current) return;
    
    // # Get country information from geography properties
    const geoProps = geo.properties || {};
    const countryIso = geoProps.ISO_A3 || geoProps.iso_a3 || geo.id;
    
    // # Get country name - prioritize the original name from the map data
    // # This preserves the actual country name instead of relying on ISO lookup
    const countryName = geoProps.name || geoProps.NAME || getCountryNameFromISO(countryIso) || 'Unknown';
    
    // # Get additional country data using map_iso - try multiple approaches to get the region
    const countryData = getCountryData(countryIso);
    
    // # Enhanced region lookup - try multiple approaches to get the correct region
    let regionName = 'Unknown';
    
    // # 1. Try to get region from country data
    if (countryData?.region && countryData.region !== 'Unknown') {
      regionName = countryData.region;
    } 
    // # 2. Try to get region directly from the country name
    else if (countryName && countryName !== 'Unknown') {
      // # Try direct lookup from getCountryRegion
      const directRegion = getCountryRegion(countryName);
      if (directRegion && directRegion !== 'Unknown') {
        regionName = directRegion;
      }
      // # Try with ISO code converted to country name
      else if (countryIso) {
        const nameFromIso = getCountryNameFromISO(countryIso);
        if (nameFromIso && nameFromIso !== 'Unknown') {
          const regionFromIsoName = getCountryRegion(nameFromIso);
          if (regionFromIsoName && regionFromIsoName !== 'Unknown') {
            regionName = regionFromIsoName;
          }
        }
      }
    }
    
    // # 3. Special case mappings for problematic countries
    const specialRegionMappings = {
      'IND': 'South Asia',
      'INDIA': 'South Asia',
      'India': 'South Asia',
      'TKM': 'Central Asia',
      'TURKMENISTAN': 'Central Asia',
      'Turkmenistan': 'Central Asia',
      'ECU': 'South America',
      'ECUADOR': 'South America',
      'Ecuador': 'South America',
      'VEN': 'South America',
      'VENEZUELA': 'South America',
      'Venezuela': 'South America',
      'COL': 'South America',
      'COLOMBIA': 'South America',
      'Colombia': 'South America',
      'PER': 'South America',
      'PERU': 'South America',
      'Peru': 'South America',
      'BOL': 'South America',
      'BOLIVIA': 'South America',
      'Bolivia': 'South America',
      'PRY': 'South America',
      'PARAGUAY': 'South America',
      'Paraguay': 'South America',
      'URY': 'South America',
      'URUGUAY': 'South America',
      'Uruguay': 'South America',
      'CHL': 'South America',
      'CHILE': 'South America',
      'Chile': 'South America',
      'ARG': 'South America',
      'ARGENTINA': 'South America',
      'Argentina': 'South America'
    };
    
    // # Apply special region mapping if we still don't have a region
    if (regionName === 'Unknown') {
      if (specialRegionMappings[countryIso]) {
        regionName = specialRegionMappings[countryIso];
      } else if (specialRegionMappings[countryName]) {
        regionName = specialRegionMappings[countryName];
      } else if (countryName && specialRegionMappings[countryName.toUpperCase()]) {
        regionName = specialRegionMappings[countryName.toUpperCase()];
      }
    }
    
    // # Get revenue for this country
    // # Try multiple approaches to match with our transformed data
    let revenue = 0;
    let paymentCount = 0; // # Initialize payment count
    
    // # First try direct ISO match
    if (countryIso && transformedData[countryIso]) {
      revenue = transformedData[countryIso];
      paymentCount = transformedPaymentData[countryIso] || 0; // # Get payment count from transformed data
    } 
    // # Try country name match
    else if (countryName && transformedData[countryName]) {
      revenue = transformedData[countryName];
      paymentCount = transformedPaymentData[countryName] || 0; // # Get payment count from transformed data
    }
    // # Try uppercase country name match
    else if (countryName && transformedData[countryName.toUpperCase()]) {
      revenue = transformedData[countryName.toUpperCase()];
      paymentCount = transformedPaymentData[countryName.toUpperCase()] || 0; // # Get payment count from transformed data
    }
    // # Try special case mappings
    else {
      // # Special case handling for problematic countries
      const specialCases = {
        'Ecuador': 'ECU',
        'Turkmenistan': 'TKM',
        'Venezuela': 'VEN',
        'Colombia': 'COL',
        'Peru': 'PER',
        'Bolivia': 'BOL',
        'Paraguay': 'PRY',
        'Uruguay': 'URY',
        'Chile': 'CHL',
        'Argentina': 'ARG'
      };
      
      if (countryName && specialCases[countryName] && transformedData[specialCases[countryName]]) {
        revenue = transformedData[specialCases[countryName]];
        paymentCount = transformedPaymentData[specialCases[countryName]] || 0; // # Get payment count from transformed data
      }
      // # Try reverse lookup
      else {
        Object.entries(specialCases).forEach(([name, iso]) => {
          if (countryIso === iso && transformedData[name]) {
            revenue = transformedData[name];
            paymentCount = transformedPaymentData[name] || 0; // # Get payment count from transformed data
          }
        });
      }
    }
    
    // # Try to find revenue in the original data if still not found
    if (revenue === 0 && countryName && financialData.revenueByCountry[countryName]) {
      revenue = financialData.revenueByCountry[countryName];
      paymentCount = financialData.paymentCountByCountry?.[countryName] || 0; // # Get payment count from original data
    }
    
    // # Debug for Turkmenistan
    if (countryName === 'Turkmenistan' || countryIso === 'TKM') {
      console.log(`Tooltip data for ${countryName}:`, {
        countryIso,
        countryName,
        revenue,
        transformedData: {
          byIso: transformedData[countryIso],
          byName: transformedData[countryName],
          byUpperName: transformedData[countryName?.toUpperCase()],
          original: financialData.revenueByCountry[countryName]
        }
      });
    }
    
    // # Calculate percentage of total revenue
    const percentage = financialData.totalRevenue > 0 
      ? ((revenue / financialData.totalRevenue) * 100).toFixed(1) 
      : '0.0';
    
    // # Get product data for this country
    // # Try multiple ways to find the country name for product lookup
    let countryNameForLookup = countryName;
    let products = {};
    
    // # 1. Try direct country name match
    if (financialData.productsByCountry[countryNameForLookup]) {
      products = financialData.productsByCountry[countryNameForLookup];
    }
    // # 2. Try with ISO code converted to country name
    else if (countryIso) {
      const nameFromIso = getCountryNameFromISO(countryIso);
      if (nameFromIso && financialData.productsByCountry[nameFromIso]) {
        products = financialData.productsByCountry[nameFromIso];
      }
    }
    // # 3. Try alternative names from country data
    else {
      const alternativeNames = countryData?.alternativeNames || [];
      for (const altName of alternativeNames) {
        if (financialData.productsByCountry[altName]) {
          products = financialData.productsByCountry[altName];
          break;
        }
      }
    }
    
    // # 4. Try special case mappings for problematic countries
    if (Object.keys(products).length === 0) {
      // # Special case handling for common countries
      const countryVariants = {
        'Saudi Arabia': ['KSA', 'Saudi', 'Kingdom of Saudi Arabia', 'SAU'],
        'United Arab Emirates': ['UAE', 'Emirates', 'ARE'],
        'United States': ['USA', 'US', 'America', 'United States of America'],
        'United Kingdom': ['UK', 'Britain', 'Great Britain', 'England', 'GBR'],
        'Turkmenistan': ['TKM'],
        'Ecuador': ['ECU'],
        'Egypt': ['EGY'],
        'India': ['IND'],
        'China': ['CHN', 'PRC', 'People\'s Republic of China'],
        'Russia': ['RUS', 'Russian Federation'],
        'Brazil': ['BRA'],
        'Canada': ['CAN'],
        'Australia': ['AUS'],
        'Germany': ['DEU', 'GER'],
        'France': ['FRA'],
        'Italy': ['ITA'],
        'Spain': ['ESP'],
        'Japan': ['JPN'],
        'South Korea': ['KOR', 'Korea, Republic of'],
        'Mexico': ['MEX'],
        'Indonesia': ['IDN'],
        'Turkey': ['TUR'],
        'Netherlands': ['NLD'],
        'Switzerland': ['CHE'],
        'Sweden': ['SWE'],
        'Poland': ['POL'],
        'Belgium': ['BEL'],
        'Norway': ['NOR'],
        'Austria': ['AUT'],
        'Israel': ['ISR'],
        'Denmark': ['DNK'],
        'Singapore': ['SGP'],
        'Hong Kong': ['HKG'],
        'Finland': ['FIN'],
        'Ireland': ['IRL'],
        'Malaysia': ['MYS'],
        'Thailand': ['THA'],
        'Philippines': ['PHL'],
        'Vietnam': ['VNM'],
        'New Zealand': ['NZL'],
        'Greece': ['GRC'],
        'Portugal': ['PRT'],
        'Czech Republic': ['CZE'],
        'Romania': ['ROU'],
        'Hungary': ['HUN'],
        'Chile': ['CHL'],
        'Colombia': ['COL'],
        'Peru': ['PER'],
        'Argentina': ['ARG'],
        'Venezuela': ['VEN'],
        // Middle East & North Africa
        'Jordan': ['JOR'],
        'Lebanon': ['LBN'],
        'Iraq': ['IRQ'],
        'Iran': ['IRN'],
        'Kuwait': ['KWT'],
        'Bahrain': ['BHR'],
        'Qatar': ['QAT'],
        'Oman': ['OMN'],
        'Yemen': ['YEM'],
        'Syria': ['SYR'],
        'Palestine': ['PSE', 'Palestinian Territories'],
        'Libya': ['LBY'],
        'Tunisia': ['TUN'],
        'Algeria': ['DZA'],
        'Morocco': ['MAR'],
        // Africa
        'South Africa': ['ZAF'],
        'Nigeria': ['NGA'],
        'Kenya': ['KEN'],
        'Ghana': ['GHA'],
        'Ethiopia': ['ETH'],
        'Tanzania': ['TZA'],
        'Uganda': ['UGA'],
        'Rwanda': ['RWA'],
        'Senegal': ['SEN'],
        'Ivory Coast': ['CIV', 'Côte d\'Ivoire'],
        'Cameroon': ['CMR'],
        'Zimbabwe': ['ZWE'],
        'Zambia': ['ZMB'],
        'Botswana': ['BWA'],
        'Namibia': ['NAM'],
        'Mozambique': ['MOZ'],
        'Angola': ['AGO'],
        'Sudan': ['SDN'],
        'South Sudan': ['SSD'],
        // Asia
        'Pakistan': ['PAK'],
        'Bangladesh': ['BGD'],
        'Sri Lanka': ['LKA'],
        'Nepal': ['NPL'],
        'Myanmar': ['MMR', 'Burma'],
        'Cambodia': ['KHM'],
        'Laos': ['LAO'],
        'Mongolia': ['MNG'],
        'North Korea': ['PRK'],
        'Taiwan': ['TWN'],
        'Bhutan': ['BTN'],
        'Brunei': ['BRN'],
        'Timor-Leste': ['TLS', 'East Timor'],
        'Maldives': ['MDV'],
        // Central Asia
        'Kazakhstan': ['KAZ'],
        'Uzbekistan': ['UZB'],
        'Kyrgyzstan': ['KGZ'],
        'Tajikistan': ['TJK'],
        // Europe
        'Ukraine': ['UKR'],
        'Belarus': ['BLR'],
        'Moldova': ['MDA'],
        'Georgia': ['GEO'],
        'Armenia': ['ARM'],
        'Azerbaijan': ['AZE'],
        'Bulgaria': ['BGR'],
        'Serbia': ['SRB'],
        'Croatia': ['HRV'],
        'Slovenia': ['SVN'],
        'Slovakia': ['SVK'],
        'Estonia': ['EST'],
        'Latvia': ['LVA'],
        'Lithuania': ['LTU'],
        'Albania': ['ALB'],
        'North Macedonia': ['MKD', 'Macedonia'],
        'Montenegro': ['MNE'],
        'Bosnia and Herzegovina': ['BIH', 'Bosnia'],
        'Kosovo': ['XKX'],
        'Cyprus': ['CYP'],
        'Malta': ['MLT'],
        'Iceland': ['ISL'],
        'Luxembourg': ['LUX'],
        // Central America & Caribbean
        'Guatemala': ['GTM'],
        'El Salvador': ['SLV'],
        'Honduras': ['HND'],
        'Nicaragua': ['NIC'],
        'Costa Rica': ['CRI'],
        'Panama': ['PAN'],
        'Cuba': ['CUB'],
        'Jamaica': ['JAM'],
        'Haiti': ['HTI'],
        'Dominican Republic': ['DOM'],
        'Trinidad and Tobago': ['TTO'],
        'Bahamas': ['BHS'],
        'Barbados': ['BRB'],
        // Oceania
        'Papua New Guinea': ['PNG'],
        'Fiji': ['FJI'],
        'Solomon Islands': ['SLB'],
        'Vanuatu': ['VUT'],
        'Samoa': ['WSM'],
        'Tonga': ['TON']
      };
      
      // # Try to find a match using country variants
      if (countryVariants[countryName]) {
        // # Try each variant
        for (const variant of countryVariants[countryName]) {
          if (financialData.productsByCountry[variant]) {
            products = financialData.productsByCountry[variant];
            console.log(`Found product data for ${countryName} under variant "${variant}":`, products);
            break;
          }
        }
      }
      
      // # Try reverse lookup - check if this country is a variant of another country
      if (Object.keys(products).length === 0) {
        for (const [mainCountry, variants] of Object.entries(countryVariants)) {
          if (variants.includes(countryName) && financialData.productsByCountry[mainCountry]) {
            products = financialData.productsByCountry[mainCountry];
            console.log(`Found product data for variant "${countryName}" under main country "${mainCountry}":`, products);
            break;
          }
        }
      }
    }
    
    // # 5. Try reverse lookup - find any country in productsByCountry that matches this ISO
    if (Object.keys(products).length === 0) {
      const possibleKeys = Object.keys(financialData.productsByCountry);
      for (const key of possibleKeys) {
        const keyIso = getCountryISO(key);
        if (keyIso === countryIso) {
          products = financialData.productsByCountry[key];
          break;
        }
      }
    }
    
    // # 6. For Saudi Arabia specifically, check if we have data under any possible name
    if (countryName === 'Saudi Arabia' && Object.keys(products).length === 0) {
      const saudiVariants = ['Saudi Arabia', 'KSA', 'Saudi', 'Kingdom of Saudi Arabia', 'SAU'];
      for (const variant of saudiVariants) {
        if (financialData.productsByCountry[variant]) {
          products = financialData.productsByCountry[variant];
          console.log(`Found Saudi Arabia product data under "${variant}":`, products);
          break;
        }
      }
    }
    
    // # 7. General fallback - try to find any product data for countries with revenue
    if (Object.keys(products).length === 0 && revenue > 0) {
      // # If we have revenue but no product data, try to create synthetic product data
      // # based on the most common products across all countries
      const allProducts = {};
      
      // # Collect all products across all countries
      Object.values(financialData.productsByCountry).forEach(countryProducts => {
        Object.entries(countryProducts).forEach(([productName, count]) => {
          allProducts[productName] = (allProducts[productName] || 0) + count;
        });
      });
      
      // # Sort products by popularity
      const popularProducts = Object.entries(allProducts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);
      
      // # Create synthetic product data with the most popular product
      if (popularProducts.length > 0) {
        products = {
          [popularProducts[0][0]]: 1
        };
        console.log(`Created synthetic product data for ${countryName} using most popular product:`, products);
      }
    }
    
    // # Debug for any country with no product data
    if (Object.keys(products).length === 0 && revenue > 0) {
      console.log(`No product data found for ${countryName} (${countryIso}) despite having revenue ${revenue}:`, {
        countryIso,
        countryName,
        revenue,
        availableCountries: Object.keys(financialData.productsByCountry)
      });
    }
    
    const topProducts = Object.entries(products)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([product, count]) => `${product}: ${count}`);
    
    // # Enhanced tooltip with region and product information
    setTooltipContent(`
      ${countryName} (${regionName})
      Revenue: ${revenue.toLocaleString()} EGP (${percentage}%)
      Payments: ${paymentCount || 0}
      ${topProducts.length > 0 ? `Top Products: ${topProducts.join(', ')}` : 'No product data'}
    `);
    
    // # Update tooltip position to follow cursor
    const rect = mapContainerRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    setTooltipPosition({ x, y });
  };

  const handleMouseLeave = () => {
    setTooltipContent('');
  };

  // # Prepare legend values
  const legendValues = {
    veryLow: Math.max(1, Math.round(maxRevenue * 0.2)),
    low: Math.max(1, Math.round(maxRevenue * 0.4)),
    medium: Math.max(1, Math.round(maxRevenue * 0.6)),
    high: Math.max(1, Math.round(maxRevenue * 0.8)),
    max: Math.max(1, Math.round(maxRevenue))
  };

  if (isLoading) {
    return <div className="fin_heat_loading">Loading financial data...</div>;
  }

  if (error && !geoData) {
    return (
      <div className="fin_heat_error">
        <p>Error: {error}</p>
        <p>Please try refreshing the page or contact support if the issue persists.</p>
      </div>
    );
  }

  if (!geoData) {
    return <div className="fin_heat_error">No geography data available. Please try refreshing the page.</div>;
  }

  return (
    <div className="fin_heat_container" ref={mapContainerRef}>
      {/* Custom Tooltip */}
      {tooltipContent && (
        <div 
          className="fin_heat_tooltip"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 40}px`,
          }}
        >
          {tooltipContent.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}

      {/* Map */}
      <div className="fin_heat_map">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 150
          }}
        >
          <ZoomableGroup
            zoom={mapPosition.zoom}
            center={mapPosition.coordinates}
            onMoveEnd={position => setMapPosition(position)}
          >
            <Geographies geography={geoData}>
              {({ geographies }) =>
                geographies.map(geo => {
                  // # Get all possible country identifiers from the geography data
                  const geoProps = geo.properties || {};
                  const countryIso = geoProps.ISO_A3 || geoProps.iso_a3;
                  const countryId = geo.id || '';
                  const countryName = geoProps.name || geoProps.NAME || '';
                  
                  // # Try to match with our transformed data using different possible identifiers
                  let revenue = 0;
                  
                  // # First try direct ISO match
                  if (countryIso && transformedData[countryIso]) {
                    revenue = transformedData[countryIso];
                  } 
                  // # Try ID match
                  else if (countryId && transformedData[countryId]) {
                    revenue = transformedData[countryId];
                  } 
                  // # Try numeric ISO code
                  else if (geoProps.iso_n3 && transformedData[geoProps.iso_n3]) {
                    revenue = transformedData[geoProps.iso_n3];
                  }
                  // # Try country name match
                  else if (countryName && transformedData[countryName]) {
                    revenue = transformedData[countryName];
                  }
                  // # Try uppercase country name match
                  else if (countryName && transformedData[countryName.toUpperCase()]) {
                    revenue = transformedData[countryName.toUpperCase()];
                  }
                  // # Try special case mappings
                  else {
                    // # Special case handling for problematic countries
                    const specialCases = {
                      'Ecuador': 'ECU',
                      'Turkmenistan': 'TKM',
                      'Venezuela': 'VEN',
                      'Colombia': 'COL',
                      'Peru': 'PER',
                      'Bolivia': 'BOL',
                      'Paraguay': 'PRY',
                      'Uruguay': 'URY',
                      'Chile': 'CHL',
                      'Argentina': 'ARG'
                    };
                    
                    if (countryName && specialCases[countryName] && transformedData[specialCases[countryName]]) {
                      revenue = transformedData[specialCases[countryName]];
                    }
                    // # Try reverse lookup
                    else {
                      Object.entries(specialCases).forEach(([name, iso]) => {
                        if (countryIso === iso && transformedData[name]) {
                          revenue = transformedData[name];
                        }
                      });
                    }
                  }
                  
                  // # Debug country data
                  if (countryName === 'Ecuador' || countryName === 'Turkmenistan') {
                    console.log(`Country data for ${countryName}:`, {
                      countryIso,
                      countryId,
                      countryName,
                      revenue,
                      transformedData: transformedData[countryIso]
                    });
                  }
                  
                  return (
                    <Geography
                      key={geo.rsmKey || countryIso || countryId}
                      geography={geo}
                      fill={revenue ? colorScale(revenue) : "#F5F5F5"}
                      stroke="#D6D6DA"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: "none" },
                        hover: { outline: "none", fill: "#F53" },
                        pressed: { outline: "none" },
                      }}
                      onMouseEnter={(event) => handleMouseEnter(geo, event)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* Legend */}
      <div className="fin_heat_legend">
        <div className="fin_heat_legend-title">Revenue Distribution</div>
        <div className="fin_heat_legend-items">
          <div className="fin_heat_legend-item">
            <div className="fin_heat_legend-color" style={{ backgroundColor: colorScale(0) }}></div>
            <span>0</span>
          </div>
          <div className="fin_heat_legend-item">
            <div className="fin_heat_legend-color" style={{ backgroundColor: colorScale(legendValues.veryLow) }}></div>
            <span>{legendValues.veryLow}</span>
          </div>
          <div className="fin_heat_legend-item">
            <div className="fin_heat_legend-color" style={{ backgroundColor: colorScale(legendValues.low) }}></div>
            <span>{legendValues.low}</span>
          </div>
          <div className="fin_heat_legend-item">
            <div className="fin_heat_legend-color" style={{ backgroundColor: colorScale(legendValues.medium) }}></div>
            <span>{legendValues.medium}</span>
          </div>
          <div className="fin_heat_legend-item">
            <div className="fin_heat_legend-color" style={{ backgroundColor: colorScale(legendValues.high) }}></div>
            <span>{legendValues.high}</span>
          </div>
          <div className="fin_heat_legend-item">
            <div className="fin_heat_legend-color" style={{ backgroundColor: colorScale(legendValues.max) }}></div>
            <span>{legendValues.max}+</span>
          </div>
        </div>
      </div>

      {/* Country Revenue Table */}
      <div className="fin_heat_table-container">
        <h3 className="fin_heat_table-title">Revenue and Payments by Country</h3>
        {isLoading ? (
          <div className="fin_heat_loading">Loading financial data...</div>
        ) : error ? (
          <div className="fin_heat_error">{error}</div>
        ) : (
          <>
            <div className="fin_heat_filters">
              <input
                type="text"
                placeholder="Search countries..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="fin_heat_search"
              />
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="fin_heat_region-filter"
              >
                {uniqueRegions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>
            <div className="fin_heat_table-wrapper">
              <table className="fin_heat_table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('country')} className="fin_heat_sortable">
                      Country {sortConfig.key === 'country' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('region')} className="fin_heat_sortable">
                      Region {sortConfig.key === 'region' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('revenue')} className="fin_heat_sortable">
                      Revenue (EGP) {sortConfig.key === 'revenue' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('payments')} className="fin_heat_sortable">
                      Payments {sortConfig.key === 'payments' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('percentage')} className="fin_heat_sortable">
                      % of Total Revenue {sortConfig.key === 'percentage' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedData.map(([country, revenue]) => {
                    const region = getCountryRegion(country) || 'Unknown';
                    const paymentCount = financialData.paymentCountByCountry?.[country] || 0;
                    const percentage = financialData.totalRevenue > 0 
                      ? ((revenue / financialData.totalRevenue) * 100).toFixed(1) 
                      : '0.0';
                    
                    return (
                      <tr key={country} className="fin_heat_table-row">
                        <td>{country}</td>
                        <td>{region}</td>
                        <td>{revenue.toLocaleString()}</td>
                        <td>{paymentCount}</td>
                        <td>{percentage}%</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="fin_heat_table-footer">
                    <td colSpan={2}><strong>Total</strong></td>
                    <td><strong>{financialData.totalRevenue?.toLocaleString() || 0}</strong></td>
                    <td><strong>{financialData.totalPayments || 0}</strong></td>
                    <td><strong>100%</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FinancialHeatMap;
